import {useState} from 'react';
import ReactDatePicker from 'react-datepicker';
import clsx from 'clsx';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import AnalyticPageView from '@di-shared/features/analytics/AnalyticPageView';
import {DEMERIT_EDUCATION_GA_SCREEN} from '@di-shared/features/public-service/demerit-education/demeritEducationGaScreenNames';
import {getDateFnsLocale, useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {DemeritEducationReservationFormType} from '@di-shared/features/public-service/demerit-education/DemeritEducationReservationForm';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import {addDate} from '@shared/dateUtils';

interface DemeritEducationReservationStep3PeriodProps {
  form: DemeritEducationReservationFormType;
  onUpdateForm(partial: Partial<DemeritEducationReservationFormType>): void;
  onNext(): void;
}

function DemeritEducationReservationStep3Period({form, onUpdateForm, onNext}: DemeritEducationReservationStep3PeriodProps) {
  const {t} = useInferableTranslation();
  const {backOrClose} = useCcWebviewUtilsHelper();

  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const [startDate, setStartDate] = useState<Date | null>();
  const [endDate, setEndDate] = useState<Date | null>();

  function changeDates(dates: [Date | null, Date | null]) {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    onUpdateForm({
      educationStartDate: start,
      educationEndDate: end,
      centerName: '',
      centerCode: '',
      centerNotice: '',
      classroomCode: '',
      educationDate: null,
      educationStartTime: null,
      educationEndTime: null,
    });
  }

  return (
    <div className="wrap">
      <AnalyticPageView screenName={DEMERIT_EDUCATION_GA_SCREEN.RESERVATION__STEP_3_PERIOD} />
      <Header left={<HeaderBackButton />}>
        <h1 className="title_xsmall_b nav_center_title">{t('T10318')}</h1>
      </Header>
      <main className="content">
        <section className="section section_inspection">
          <div className="inspection_info">
            <div className="inspection_info">
              <p className="place_text">
                <span className="ic ic_basic_location_16px" />
                <span className={clsx('region', {is_select: form.branchName})}>{form.branchName || t('T10272')}</span>
                <span className={clsx('location', {is_select: form.centerName})}>{form.centerName || t('T10261')}</span>
              </p>
              <p
                className={clsx('inspection_text', {
                  is_select: (form.educationStartDate && form.educationEndDate) || (form.educationStartTime && form.educationEndTime),
                })}
              >
                <span className="ic ic_calendar_16px" />
                <span className={clsx('date', {is_select: form.educationStartDate && form.educationEndDate})}>
                  {form.educationStartDate && form.educationEndDate
                    ? `${CldrDatetimeFormatter.format(form.educationStartDate, 'Md')} - ${CldrDatetimeFormatter.format(
                        form.educationEndDate,
                        'Md'
                      )}`
                    : t('T10320')}
                </span>
                <span className={clsx('time', {is_select: form.educationStartTime && form.educationEndTime})}>
                  {form.educationStartTime && form.educationEndTime
                    ? `${CldrDatetimeFormatter.format(form.educationStartTime, 'aHM')} - ${CldrDatetimeFormatter.format(
                        form.educationEndTime,
                        'aHM'
                      )}`
                    : t('T10276')}
                </span>
              </p>
            </div>
            <ReactDatePicker
              inline
              selectsRange
              disabledKeyboardNavigation
              locale={getDateFnsLocale()}
              minDate={addDate(new Date(), {days: 1})} // 당일 예약 불가
              selected={startDate}
              startDate={startDate}
              endDate={endDate}
              onChange={(date) => changeDates(date)}
              renderCustomHeader={({date, prevMonthButtonDisabled, nextMonthButtonDisabled, decreaseMonth, increaseMonth}) => (
                <div className="react-datepicker__current-month">
                  <button
                    type="button"
                    aria-label="Previous Month"
                    className={clsx('react-datepicker__navigation react-datepicker__navigation--previous', {
                      'navigation--disabled': prevMonthButtonDisabled,
                    })}
                    disabled={prevMonthButtonDisabled}
                    onClick={() => decreaseMonth()}
                  >
                    <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--previous">{t('T10100')}</span>
                  </button>
                  {CldrDatetimeFormatter.format(date, 'yMMM')}
                  <button
                    type="button"
                    aria-label="Next Month"
                    className={clsx('react-datepicker__navigation react-datepicker__navigation--next', {
                      'navigation--disabled': nextMonthButtonDisabled,
                    })}
                    disabled={nextMonthButtonDisabled}
                    onClick={() => increaseMonth()}
                  >
                    <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--next">{t('T10101')}</span>
                  </button>
                </div>
              )}
            />
          </div>
        </section>
      </main>
      <div className="floating_action_area">
        <div className="button_box_complex">
          <button
            type="button"
            className="btn btn_large btn_outline btn_full"
            onClick={backOrClose}
          >
            {t('T10316')}
          </button>
          <button
            type="button"
            className="btn btn_large btn_filled btn_full"
            disabled={!(form.educationStartDate && form.educationEndDate)}
            onClick={onNext}
          >
            {t('T20348')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DemeritEducationReservationStep3Period;
